import type {
  AutomationTemplatePublishSource,
  StepTemplatePublishSource,
  WorkflowTemplatePublishSource,
} from '../../desktop/dialogs/marketplace/publish-source';
import type { SlotDescriptionsState } from '../../desktop/dialogs/marketplace/slot-description-editor';

export interface TemplatePublishSlot {
  id: string;
  name: string;
  type: string;
  required?: boolean;
  description?: string;
  descriptionsI18n?: Record<string, string>;
}

export interface TemplatePublishStepNode {
  id: string;
  name?: string;
  type?: string;
  inputSlots?: readonly TemplatePublishSlot[];
  configSlots?: readonly TemplatePublishSlot[];
  outputSlots?: readonly TemplatePublishSlot[];
  parameters?: Record<string, unknown>;
}

export function buildAutomationTemplatePublishSource(input: {
  workspaceId: string;
  automationBlockId: string;
  workflowId?: string;
  title?: string;
}): AutomationTemplatePublishSource {
  return {
    kind: 'automation',
    workspaceId: input.workspaceId,
    automationBlockId: input.automationBlockId,
    ...(input.workflowId ? { workflowId: input.workflowId } : {}),
    title: input.title,
  };
}

export function buildWorkflowTemplatePublishSource(input: {
  workspaceId: string;
  workflowId: string;
  title?: string;
}): WorkflowTemplatePublishSource {
  return {
    kind: 'workflow',
    workspaceId: input.workspaceId,
    workflowId: input.workflowId,
    title: input.title,
  };
}

export function buildStepTemplatePublishSource(input: {
  workspaceId: string;
  workflowId: string;
  node: TemplatePublishStepNode;
}): StepTemplatePublishSource {
  return {
    kind: 'step',
    workspaceId: input.workspaceId,
    workflowId: input.workflowId,
    stepId: input.node.id,
    title: input.node.name,
    isAiStep: input.node.type === 'swr.ai',
    stepInsertType: resolveManagedStepInsertType(input.node.type),
    inputSlots: normalizeSlots(input.node.inputSlots),
    configSlots: normalizeSlots(input.node.configSlots),
    outputSlots: normalizeSlots(input.node.outputSlots),
    initialSlotDescriptions: collectInitialSlotDescriptions(input.node),
    existingTemplateId: getManagedTemplateId(input.node),
  };
}

function resolveManagedStepInsertType(
  nodeType: string | undefined
): 'managed_http' | 'managed_code' | 'managed_ai' | undefined {
  switch (nodeType) {
    case 'swr.http':
      return 'managed_http';
    case 'swr.code':
      return 'managed_code';
    case 'swr.ai':
      return 'managed_ai';
    default:
      return undefined;
  }
}

export function canOpenWorkflowTemplatePublish(input: {
  publishAllowed: boolean;
  canPublish: boolean;
  workflowId: string | null | undefined;
}): boolean {
  return (
    input.publishAllowed &&
    input.canPublish &&
    typeof input.workflowId === 'string' &&
    input.workflowId.length > 0
  );
}

export function canOpenAutomationTemplatePublish(input: {
  publishAllowed: boolean;
  canPublish: boolean;
  automationBlockId: string | null | undefined;
  executionStatus: string | null | undefined;
}): boolean {
  return (
    input.publishAllowed &&
    input.canPublish &&
    input.executionStatus === 'completed' &&
    typeof input.automationBlockId === 'string' &&
    input.automationBlockId.length > 0
  );
}

function normalizeSlots(slots: readonly TemplatePublishSlot[] | undefined) {
  return (slots ?? []).map(slot => ({
    id: slot.id,
    name: slot.name,
    type: slot.type,
    required: slot.required === true,
  }));
}

function collectInitialSlotDescriptions(
  node: TemplatePublishStepNode
): SlotDescriptionsState {
  return Object.fromEntries(
    [
      ...(node.inputSlots ?? []),
      ...(node.configSlots ?? []),
      ...(node.outputSlots ?? []),
    ]
      .filter(slot => slot.descriptionsI18n || slot.description)
      .map(slot => [
        slot.id,
        {
          ...(slot.descriptionsI18n ?? {}),
          ...(slot.description ? { de: slot.description } : {}),
        },
      ])
  );
}

function getManagedTemplateId(
  node: TemplatePublishStepNode
): string | undefined {
  return typeof node.parameters?.['__managedTemplateId'] === 'string'
    ? String(node.parameters['__managedTemplateId'])
    : undefined;
}
